import { useCallback, useEffect, useState } from 'react';
import { Input, Form, Tabs, Radio, Tooltip, Collapse } from 'antd';
const { TextArea } = Input;
import { Button } from '../baseComponents/Button';
import { analytics } from 'src/services';
import {
  createDotPhraseOverride,
  updateDotPhraseOverride,
  deleteDotPhraseOverride,
  listDotPhraseOverrides,
} from '../../store/actions/settings';
import { selectedDotPhraseOverridesSlice } from '../../store/slices/settings';
import { useSelector } from 'react-redux';
import { useDispatch } from 'react-redux';
import { ModalComponent } from '../baseComponents';
import { AlertIcon } from 'src/icons';
import { SupaDemo } from '../../../../components/groupComponents/supaDemo';
import { QuestionCircleOutlined } from '@ant-design/icons';
import { useAuth } from '@frontegg/react';

export const DotPhrases = () => {
  const [form] = Form.useForm();
  const isMobileView = window.innerWidth <= 1260;
  const [isModalVisible, setIsModalVisible] = useState(false);
  const { dotPhraseOverridesList, selectedDotPhraseOverrides } = useSelector(
    (state) => state?.settingsState
  );
  const dispatch = useDispatch();
  const { user } = useAuth();
  const [submitLoading, setSubmitLoading] = useState(false);

  const handleSelectDotPhrase = useCallback(
    (dotPhraseOverridesId) => {
      const phrase = dotPhraseOverridesList.find(
        (phrase) =>
          phrase.dot_phrase_overrides_id == dotPhraseOverridesId ||
          phrase.dot_phrase_id == dotPhraseOverridesId
      );
      dispatch(selectedDotPhraseOverridesSlice(phrase));
      form.setFieldsValue(phrase);
    },
    [dispatch, dotPhraseOverridesList, form]
  );

  useEffect(() => {
    dispatch(listDotPhraseOverrides());
  }, [dispatch]);

  useEffect(() => {
    if (selectedDotPhraseOverrides?.dot_phrase_overrides_id) {
      handleSelectDotPhrase(selectedDotPhraseOverrides.dot_phrase_overrides_id);
    } else if (dotPhraseOverridesList?.length > 0) {
      handleSelectDotPhrase(dotPhraseOverridesList[0]?.dot_phrase_overrides_id);
    }
  }, [
    dotPhraseOverridesList,
    dispatch,
    form,
    selectedDotPhraseOverrides?.dot_phrase_overrides_id,
    handleSelectDotPhrase,
  ]);

  const handleAddNewDotPhrase = () => {
    analytics.track('Added New Dot Phrase', { email: user?.email });
    const newPhrase = {
      title: 'New dot phrase',
      phrase: '',
      template: '',
    };
    dispatch(createDotPhraseOverride(newPhrase));
  };

  const handleEditDotPhrase = (values) => {
    const hasChanged = Object.keys(values).some(
      (key) => values[key] !== selectedDotPhraseOverrides[key]
    );

    if (selectedDotPhraseOverrides?.dot_phrase_id) {
      dispatch(
        updateDotPhraseOverride(
          selectedDotPhraseOverrides?.dot_phrase_id,
          values
        )
      );
    } else if (
      selectedDotPhraseOverrides?.dot_phrase_overrides_id &&
      hasChanged
    ) {
      dispatch(
        updateDotPhraseOverride(
          selectedDotPhraseOverrides?.dot_phrase_overrides_id,
          values
        )
      );
    }
  };

  const handleDeleteDotPhrase = () => {
    setSubmitLoading(true);
    analytics.track('Deleted Dot Phrase', {
      DotphraseId: selectedDotPhraseOverrides?.dot_phrase_overrides_id,
      email: user?.email,
    });
    dispatch(
      deleteDotPhraseOverride(
        selectedDotPhraseOverrides?.dot_phrase_overrides_id,
        setSubmitLoading
      )
    );
    setIsModalVisible(false);
  };

  const handleDeleteDotPhraseOpen = () => {
    setIsModalVisible(true);
  };

  const handleDeleteDotPhraseCancel = () => {
    setSubmitLoading(false);
    setIsModalVisible(false);
  };

  const isDotPhraseOverride =
    selectedDotPhraseOverrides?.dot_phrase_overrides_id;
  const isMobile = window.innerWidth < 1260;
  return (
    <div
      id="dot-phrases"
      className={`${
        isMobile ? 'mb-16' : ''
      } w-full p-7 dot-phrases shadow-md bg-white rounded-xl xl:w-10/12 font-sans`}
    >
      <div className="add-new-phrase flex justify-between">
        <SupaDemo>
          <span
            data-supademo-demo="clzw69kjh01r9w28qgbi8piho"
            className="cursor-pointer ih-button-ghost gap-2 text-[#5a8dda] underline"
            onClick={() => {
              analytics.track('Clicked Dot Phrases Guided Tour', {
                email: user?.email,
              });
            }}
          >
            <QuestionCircleOutlined /> Guided Tour
          </span>
        </SupaDemo>
        <Button type="button" onClick={handleAddNewDotPhrase}>
          Add new
        </Button>
      </div>
      <Tabs
        tabPosition={isMobileView ? 'top' : 'left'}
        onTabClick={handleSelectDotPhrase}
        activeKey={
          selectedDotPhraseOverrides?.dot_phrase_overrides_id
            ? selectedDotPhraseOverrides?.dot_phrase_overrides_id
            : selectedDotPhraseOverrides?.dot_phrase_id
        }
      >
        {dotPhraseOverridesList &&
          dotPhraseOverridesList.map((phrase) => (
            <Tabs.TabPane
              tab={phrase.title}
              key={
                phrase.dot_phrase_overrides_id
                  ? phrase.dot_phrase_overrides_id
                  : phrase.dot_phrase_id
              }
            >
              <Form
                form={form}
                layout="vertical"
                onFinish={handleEditDotPhrase}
              >
                <Form.Item
                  name="title"
                  label={<span className="font-semibold">Name</span>}
                  rules={[
                    {
                      required: true,
                      message: 'dot phrase name cannot be empty',
                    },
                  ]}
                >
                  <Input placeholder="Dot phrase name" className="font-sans" />
                </Form.Item>
                <Form.Item
                  name="phrase"
                  label={
                    <div className="flex gap-3 font-semibold">
                      Dictation phrase
                    </div>
                  }
                >
                  <Input
                    placeholder="Phrase for recognizing in dictation mode"
                    className="font-sans"
                  />
                </Form.Item>
                <Form.Item
                  name="template"
                  label={<span className="font-semibold">Text</span>}
                  rules={[
                    { required: true, message: 'template cannot be empty' },
                  ]}
                >
                  <TextArea
                    placeholder="Your dot phrase template"
                    rows={12}
                    className="font-sans"
                  />
                </Form.Item>
                <Collapse
                  items={[
                    {
                      key: '1',
                      label: (
                        <div className="font-semibold text-md">
                          Additional Settings
                        </div>
                      ),
                      children: (
                        <Form.Item
                          name="section_name"
                          label={
                            <div className="flex gap-3 font-semibold">
                              Section name
                              <Tooltip
                                title={
                                  'Choose where to place this dot phrase in the note when uttering "insert [phrase]" during an encounter.'
                                }
                              >
                                <QuestionCircleOutlined className="text-gray-400 cursor-help" />
                              </Tooltip>
                            </div>
                          }
                        >
                          <Radio.Group className="flex flex-col gap-2">
                            <Radio value="objective">Objective</Radio>
                            <Radio value="physical_exam">Physical Exam</Radio>
                            <Radio value="assessment">Assessment</Radio>
                            <Radio value="plan">Plan</Radio>
                          </Radio.Group>
                        </Form.Item>
                      ),
                    },
                  ]}
                ></Collapse>
              </Form>
              <div className="flex justify-end font-sans mt-4">
                {isDotPhraseOverride && (
                  <Button
                    type="cancel"
                    className=" !bg-[#d63232] hover:!bg-[#a30000] !border-[#d63232] hover:!border-[#a30000] "
                    onClick={handleDeleteDotPhraseOpen}
                  >
                    Delete
                  </Button>
                )}

                <Button
                  type="button"
                  onClick={() => {
                    form.submit();
                    analytics.track('Clicked Save Dot Phrase', {
                      email: user?.email,
                    });
                  }}
                  className="save-button ml-4"
                >
                  {isDotPhraseOverride ? 'Save' : 'Save as copy'}
                </Button>
              </div>
            </Tabs.TabPane>
          ))}
      </Tabs>
      <ModalComponent
        centered={true}
        open={isModalVisible}
        handleSubmit={handleDeleteDotPhrase}
        handleCancel={handleDeleteDotPhraseCancel}
        submitButtonText="Delete"
        cancelButtonText="Cancel"
        closable={false}
        className={'remove-modal'}
        submitLoading={submitLoading}
        additionalBody={
          <>
            {' '}
            <div className="text-black font-sans font-medium text-lg flex justify-center items-center gap-2">
              <span>
                {' '}
                <AlertIcon />
              </span>

              <p>Are you sure you want to delete this dot phrase?</p>
            </div>
          </>
        }
      />
    </div>
  );
};
